// A customer's Estimates tab (customers.estimates; Amir, 22 Oct): every estimate shown to them, job by
// job, and which one they picked. ServiceTitan's come over one estimate per option it showed, the one
// bought sold; Flowology's own may carry several options, one selected when it sells.

export interface EstimateOption {
  id: string;
  title: string;
  /** Before tax, its tax, and the total with tax (ServiceTitan shows the total). */
  subtotalCents?: number;
  taxCents?: number;
  totalCents: number;
  selected: boolean;
}

export interface CustomerEstimate {
  id: string;
  number: string;
  title: string;
  jobId: string;
  jobNumber: string;
  jobType: string;
  locationId: string;
  status: 'draft' | 'presented' | 'sent' | 'sold' | 'declined' | 'expired';
  soldLater: boolean;
  presentedAt: string | null;
  soldAt: string | null;
  soldBy: string | null;
  imported: boolean;
  /** ServiceTitan took it off after the customer saw it (history fix 2). */
  removedInServiceTitan?: boolean;
  options: EstimateOption[];
}

export interface JobEstimates {
  jobId: string;
  jobNumber: string;
  jobType: string;
  /** One of its estimates was bought. */
  picked: boolean;
  estimates: CustomerEstimate[];
}

/** The estimates job by job, in the order the server gave them (newest job first). */
export function byJob(list: readonly CustomerEstimate[]): JobEstimates[] {
  const out = new Map<string, JobEstimates>();
  for (const e of list) {
    const j = out.get(e.jobId) ?? {
      jobId: e.jobId,
      jobNumber: e.jobNumber,
      jobType: e.jobType,
      picked: false,
      estimates: [],
    };
    j.estimates.push(e);
    if (e.status === 'sold') j.picked = true;
    out.set(e.jobId, j);
  }
  return [...out.values()];
}

/** What became of an estimate, in the office's words, and its tone. */
export function outcome(
  e: Pick<CustomerEstimate, 'status' | 'soldLater'>,
  jobPicked: boolean,
): { words: string; tone: 'ok' | 'warn' | '' } {
  switch (e.status) {
    case 'sold':
      return { words: e.soldLater ? 'Picked, for a later visit' : 'Picked', tone: 'ok' };
    case 'declined':
      return { words: 'Declined', tone: '' };
    case 'expired':
      return { words: 'Expired', tone: '' };
    case 'draft':
      return { words: 'Draft, not shown', tone: '' };
    default:
      // shown or sent, never answered: another on the job was the one bought, or it's still open
      return jobPicked
        ? { words: 'Not picked', tone: '' }
        : { words: e.status === 'sent' ? 'Sent, no answer yet' : 'Shown, no answer yet', tone: 'warn' };
  }
}

/**
 * What an estimate from ServiceTitan says beside its number (history fix 1, 2): "From ServiceTitan"
 * while the company mirrors it, and "Deleted in ServiceTitan" on one ServiceTitan deleted (kept only as a
 * job's one sale: DEC-007), whatever the mode. Nothing on Flowology's own.
 */
export function stEstimateWords(
  e: { imported?: boolean; removedInServiceTitan?: boolean },
  mirroring: boolean,
): string[] {
  if (!e.imported) return [];
  return [
    ...(mirroring ? ['From ServiceTitan'] : []),
    ...(e.removedInServiceTitan ? ['Deleted in ServiceTitan'] : []),
  ];
}
